import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ComingSoon } from '../components/ComingSoon';
import { ExportMenu } from '../components/ExportMenu';
import { FilterBar, type ContractorOption } from '../components/FilterBar';
import { FolderOpenIcon, MailIcon, PlusIcon } from '../components/icons';
import { KpiCards, KpiSkeleton } from '../components/KpiCards';
import { MarkPaidDialog } from '../components/MarkPaidDialog';
import { RecordOutsidePaymentDialog, type OutsideSubmitResult } from '../components/RecordOutsidePaymentDialog';
import { RecordsTable } from '../components/RecordsTable';
import { VoidDialog } from '../components/VoidDialog';
import { describeRecordFailure } from '../errors';
import { formatMoney, recordWritesAvailableInBuild } from '../format';
import { contractorPaymentRecordsGateway as defaultGateway, type ContractorPaymentRecordsGateway } from '../index';
import { NO_FILTERS, PAGE_SIZE_OPTIONS, filtersActive, listStateToParams, parseListState, toRecordQuery, type RecordFilters } from '../query';
import { saveExportedFile } from '../saveFile';
import type { ExportFormat, OutsidePaymentInput, PaymentRecordPage, PaymentRecordSummary, PaymentRecordItem, SortDir, SortField } from '../types';
import { usePermissionCheck } from '../../identity/state/usePermissions';
import { peopleGateway } from '../../people';
import { requestNotificationsRefresh } from '../../notifications/useStoredNotifications';
import '../contractor-payments.css';

const SEARCH_DEBOUNCE_MS = 350;
export const LIST_PATH = '/payroll/contractor-payments';

type Dialog = { kind: 'outside' } | { kind: 'paid'; record: PaymentRecordItem } | { kind: 'void'; record: PaymentRecordItem } | null;
type SummaryLoad = { kind: 'loading' } | { kind: 'unavailable'; message: string } | { kind: 'loaded'; summary: PaymentRecordSummary };

/**
 * Contractor Payments (Figma "Payments - Payment List"): the history and records page downstream of Pay Contractors. One row per contractor payment: an
 * approved Pay Contractors line (Escochex) or a payment recorded here that was made outside Escochex. The SERVER filters, sorts, pages, computes the YTD
 * column and the KPI cards, and says which actions each row allows; this page asks and shows. Filters, sort and page live in the address, so the statement
 * preview's "Back to payment list" restores them. Nothing here moves money: Paid means recorded as paid.
 */
export function ContractorPaymentsListPage({ gateway = defaultGateway }: { gateway?: ContractorPaymentRecordsGateway }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const can = usePermissionCheck();
  const state = useMemo(() => parseListState(params), [params]);
  const year = useMemo(() => new Date().getFullYear(), []);
  const [searchText, setSearchText] = useState(state.filters.search);
  const [data, setData] = useState<PaymentRecordPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<SummaryLoad>({ kind: 'loading' });
  const [contractors, setContractors] = useState<ContractorOption[]>([]);
  const [nonce, setNonce] = useState(0);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const writesAvailable = recordWritesAvailableInBuild(import.meta.env);
  const canRecord = writesAvailable && can('paymentbatches.write');

  const query = useMemo(() => toRecordQuery(state), [state]);

  const update = useCallback((next: typeof state) => setParams(listStateToParams(next), { replace: true }), [setParams]);

  // ---- records ------------------------------------------------------------------------------------------------------------------------------
  useEffect(() => {
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    setError(null);
    const request = query ? gateway.list(query, controller.signal) : Promise.resolve<PaymentRecordPage>({ items: [], page: 1, pageSize: state.pageSize, totalCount: 0 });
    request
      .then((result) => { if (!controller.signal.aborted) setData(result); })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(describeRecordFailure(err, 'The contractor payments couldn’t be loaded. Try again.').message);
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [gateway, query, state.pageSize, nonce]);

  // ---- KPI cards: the whole company for the year, independent of the filters --------------------------------------------------------------------
  useEffect(() => {
    const controller = new AbortController();
    gateway
      .summary(year, controller.signal)
      .then((s) => { if (!controller.signal.aborted) setSummary({ kind: 'loaded', summary: s }); })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setSummary({ kind: 'unavailable', message: describeRecordFailure(err, 'The summary cards couldn’t be loaded.').message });
      });
    return () => controller.abort();
  }, [gateway, year, nonce]);

  useEffect(() => {
    let cancelled = false;
    peopleGateway
      .listContractors({ page: 1, pageSize: 100 })
      .then((page) => { if (!cancelled) setContractors(page.items.map((c) => ({ id: c.id, name: c.displayName })).sort((a, b) => a.name.localeCompare(b.name))); })
      .catch(() => { /* the Contractor Name filter simply lists nobody; Search still works */ });
    return () => { cancelled = true; };
  }, [nonce]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  // ---- filters / sort / paging ---------------------------------------------------------------------------------------------------------------
  function setFilter<K extends keyof RecordFilters>(key: K, value: RecordFilters[K]) {
    update({ ...state, filters: { ...state.filters, [key]: value }, page: 1 });
  }
  function onSearch(text: string) {
    setSearchText(text);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setFilter('search', text), SEARCH_DEBOUNCE_MS);
  }
  function resetFilters() {
    if (timer.current) clearTimeout(timer.current);
    setSearchText('');
    update({ ...state, filters: NO_FILTERS, page: 1 });
  }
  function onSort(next: { sort: SortField; dir: SortDir }) {
    update({ ...state, ...next, page: 1 });
  }

  const total = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / state.pageSize));
  const currentPage = Math.min(state.page, totalPages);
  const rows = data?.items ?? [];
  const firstIndex = (currentPage - 1) * state.pageSize;
  const active = filtersActive(state.filters);
  const loaded = !error && data !== null;
  const companyEmpty = loaded && total === 0 && !active;
  const noResults = loaded && total === 0 && active;
  const listSearch = location.search;

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  // ---- actions -----------------------------------------------------------------------------------------------------------------------------------
  async function recordOutside(input: OutsidePaymentInput): Promise<OutsideSubmitResult> {
    try {
      const saved = await gateway.recordOutside(input);
      setDialog(null);
      setNotice(`Outside payment of ${formatMoney(saved.amount)} recorded for ${saved.contractorName}. It is marked Paid; no money was moved.`);
      requestNotificationsRefresh();
      refresh();
      return null;
    } catch (err) {
      const failure = describeRecordFailure(err, 'The payment couldn’t be recorded. Nothing was saved. Try again.');
      return { message: failure.message, duplicate: failure.duplicate };
    }
  }

  async function markPaid(record: PaymentRecordItem, paidOn: string, reference: string | null): Promise<string | null> {
    try {
      await gateway.markPaid(record.id, paidOn, reference);
    } catch (err) {
      return describeRecordFailure(err, 'The payment couldn’t be marked as paid. Try again.').message;
    }
    setDialog(null);
    setNotice(`${record.contractorName}’s payment is marked Paid. This is a record only: no money was moved.`);
    refresh();
    return null;
  }

  async function voidRecord(record: PaymentRecordItem, reason: string): Promise<string | null> {
    try {
      await gateway.voidRecord(record.id, reason);
    } catch (err) {
      return describeRecordFailure(err, 'The payment couldn’t be voided. Try again.').message;
    }
    setDialog(null);
    setNotice(`${record.contractorName}’s payment is voided. The record is kept for the audit trail.`);
    requestNotificationsRefresh();
    refresh();
    return null;
  }

  async function exportAs(format: ExportFormat) {
    const q = toRecordQuery(state);
    if (!q) return setExportError('No records match these filters, so there is nothing to export.');
    setExporting(true);
    setExportError(null);
    try {
      saveExportedFile(await gateway.exportRecords(format, q));
    } catch (err) {
      setExportError(describeRecordFailure(err, 'The export couldn’t be created. Try again.').message);
    } finally {
      setExporting(false);
    }
  }

  const go = (to: string, withPrint = false) => navigate({ pathname: to, search: withPrint ? '?print=1' : '' }, { state: { listSearch } });

  return (
    <section className="cp-page cp-list" aria-labelledby="cp-heading" aria-busy={loading} data-testid="contractor-payments">
      <div className="cp-head">
        <h2 id="cp-heading" className="cp-title">Contractor Payments</h2>
      </div>

      <div className="cp-panel">
        {summary.kind === 'loaded' && <KpiCards summary={summary.summary} />}
        {summary.kind === 'loading' && <KpiSkeleton message="Loading summary…" />}
        {summary.kind === 'unavailable' && <KpiSkeleton message={summary.message} />}

        <FilterBar
          filters={state.filters} searchText={searchText} contractors={contractors} onSearch={onSearch} onChange={setFilter} disabled={!!error}
          actions={(
            <>
              {canRecord && (
                <button type="button" className="cp-btn cp-btn--primary-orange cp-actions__record" onClick={() => setDialog({ kind: 'outside' })}>
                  <PlusIcon /> Record Outside Payment
                </button>
              )}
              <div className="cp-actions__secondary">
                <ComingSoon label="Email" reason="Email is coming soon" className="cp-btn cp-btn--outline"><MailIcon /> Email</ComingSoon>
                <ExportMenu onExport={(f) => void exportAs(f)} busy={exporting} disabled={!!error || companyEmpty} />
              </div>
            </>
          )}
        />

        {notice && <p className="success cp-notice" role="status" data-testid="list-notice">{notice}</p>}
        {exportError && <p className="error cp-notice" role="alert" data-testid="export-error">{exportError}</p>}

        {loading && !data && <p className="cp-muted" role="status">Loading contractor payments…</p>}
        {error && (
          <div className="cp-error" role="alert">
            <p className="error" data-testid="load-error">{error}</p>
            <button type="button" className="cp-btn cp-btn--outline" onClick={refresh}>Try again</button>
          </div>
        )}

        {loaded && !companyEmpty && (
          <div className="cp-bar">
            <label className="cp-rows">
              Rows per page:
              <select value={state.pageSize} onChange={(e) => update({ ...state, pageSize: Number(e.target.value), page: 1 })}>
                {PAGE_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <span className="cp-count" role="status" aria-live="polite">
              {rows.length > 0 ? `${firstIndex + 1}-${firstIndex + rows.length} of ${total} ${total === 1 ? 'item' : 'items'}` : '0 items'}
            </span>
            {rows.length > 0 && (
              <nav className="cp-paging" aria-label="Pagination">
                <button type="button" aria-label="First page" disabled={currentPage <= 1} onClick={() => update({ ...state, page: 1 })}>«</button>
                <button type="button" disabled={currentPage <= 1} onClick={() => update({ ...state, page: currentPage - 1 })}>Previous</button>
                <span className="cp-page-no">Page {currentPage} of {totalPages}</span>
                <button type="button" disabled={currentPage >= totalPages} onClick={() => update({ ...state, page: currentPage + 1 })}>Next</button>
                <button type="button" aria-label="Last page" disabled={currentPage >= totalPages} onClick={() => update({ ...state, page: totalPages })}>»</button>
              </nav>
            )}
          </div>
        )}

        {loaded && !companyEmpty && rows.length > 0 && <p className="cp-scroll-hint">Scroll sideways to see every column. The Contractor column stays in view.</p>}
        {loaded && !companyEmpty && rows.length > 0 && (
          <RecordsTable
            items={rows} sort={state.sort} dir={state.dir} onSort={onSort} writesAvailable={writesAvailable}
            onView={(r) => go(`${LIST_PATH}/${r.id}`)} onPrint={(r) => go(`${LIST_PATH}/${r.id}`, true)}
            onMarkPaid={(r) => setDialog({ kind: 'paid', record: r })} onVoid={(r) => setDialog({ kind: 'void', record: r })}
          />
        )}

        {companyEmpty && (
          <div className="cp-empty" data-testid="empty-company">
            <FolderOpenIcon />
            <p className="cp-empty__title">No Data Available</p>
            <p className="cp-muted">No contractor payment records available yet.</p>
          </div>
        )}
        {noResults && (
          <div className="cp-empty" data-testid="empty-filtered">
            <FolderOpenIcon />
            <p className="cp-empty__title">No payments match your filters.</p>
            <button type="button" className="link" onClick={resetFilters}>Reset filters</button>
          </div>
        )}
      </div>

      {dialog?.kind === 'outside' && <RecordOutsidePaymentDialog onClose={() => setDialog(null)} onSubmit={recordOutside} />}
      {dialog?.kind === 'paid' && <MarkPaidDialog record={dialog.record} onClose={() => setDialog(null)} onConfirm={(d, r) => markPaid(dialog.record, d, r)} />}
      {dialog?.kind === 'void' && <VoidDialog record={dialog.record} onClose={() => setDialog(null)} onConfirm={(reason) => voidRecord(dialog.record, reason)} />}
    </section>
  );
}
